import { trackEvent } from "@/lib/analytics";
import { dpopApi } from "@/lib/dpop-client";

export type ShareLinkType = "referral" | "pay" | "vault" | "report" | "form";

const SHARE_REF_KEY = "fexr_share_ref";
const VISIT_DAYS_KEY = "fexr_link_visit_days";
const ANALYTICS_QUEUE_KEY = "fexr_analytics_queue";
const VISIT_DAY_RETENTION = 60; // days

interface StashedShareRef {
  ref: string | null;
  src: string | null;
  timestamp: number;
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10); // yyyy-mm-dd
}

// ─── captureInboundShareRef ───────────────────────────────────────────────────

// Read + stash inbound attribution. Call once on landing pages.
// Reads ?ref= & ?src= (and ?join= for referral codes) from the given
// URLSearchParams; stashes {ref, src} in sessionStorage key
// "fexr_share_ref" (with timestamp, replace if newer than existing).
export function captureInboundShareRef(params: URLSearchParams): void {
  if (typeof window === "undefined") return;

  const ref = params.get("ref") ?? params.get("join");
  const src = params.get("src");
  if (!ref && !src) return;

  try {
    const existingRaw = sessionStorage.getItem(SHARE_REF_KEY);
    const existing: StashedShareRef | null = existingRaw
      ? JSON.parse(existingRaw)
      : null;
    const now = Date.now();
    // Replace only if the new attribution is newer than the existing stash
    if (
      existing &&
      typeof existing.timestamp === "number" &&
      existing.timestamp > now
    )
      return;

    const next: StashedShareRef = {
      ref: ref ?? existing?.ref ?? null,
      src: src ?? existing?.src ?? null,
      timestamp: now,
    };
    sessionStorage.setItem(SHARE_REF_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable — attribution silently degrades
  }
}

// ─── getShareRef ──────────────────────────────────────────────────────────────

// Current stashed attribution, or nulls.
export function getShareRef(): { ref: string | null; src: string | null } {
  if (typeof window === "undefined") return { ref: null, src: null };
  try {
    const raw = sessionStorage.getItem(SHARE_REF_KEY);
    if (!raw) return { ref: null, src: null };
    const parsed: StashedShareRef = JSON.parse(raw);
    return { ref: parsed.ref ?? null, src: parsed.src ?? null };
  } catch {
    return { ref: null, src: null };
  }
}

// ─── trackLinkVisit ───────────────────────────────────────────────────────────

interface VisitDayEntry {
  day: string; // yyyy-mm-dd
  key: string; // `${linkType}:${linkId}:${day}`
}

function readVisitDays(): VisitDayEntry[] {
  try {
    const raw = localStorage.getItem(VISIT_DAYS_KEY);
    if (!raw) return [];
    const parsed: VisitDayEntry[] = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - VISIT_DAY_RETENTION);
    const cutoffKey = cutoff.toISOString().slice(0, 10);
    return parsed.filter(
      (e) => e && typeof e.key === "string" && e.day >= cutoffKey,
    );
  } catch {
    return [];
  }
}

function writeVisitDays(entries: VisitDayEntry[]) {
  try {
    localStorage.setItem(VISIT_DAYS_KEY, JSON.stringify(entries));
  } catch {}
}

// Record a visit to a shared link. Dedupes per (linkType:linkId) per day
// via a localStorage key set so page refreshes don't inflate counts, then
// trackEvent("shared_link_visited", { link_type, link_id, ref, src }).
export function trackLinkVisit(linkType: ShareLinkType, linkId: string): void {
  if (typeof window === "undefined" || !linkId) return;

  const day = todayKey();
  const dedupeKey = `${linkType}:${linkId}:${day}`;
  const entries = readVisitDays();
  if (entries.some((e) => e.key === dedupeKey)) return; // already counted today

  writeVisitDays([...entries, { day, key: dedupeKey }]);

  const { ref, src } = getShareRef();
  trackEvent("shared_link_visited", {
    link_type: linkType,
    link_id: linkId,
    ref,
    src,
  });
}

// ─── One-link (fexr.io/{token}) ───────────────────────────────────────────────
//
// Every share button asks the API for the link (POST /v1/journeys/links) and
// shares `link.url` exactly as returned. Never build fexr.io/... or app URLs
// here: issuance is deterministic per target, creator and channel, so asking
// again returns the same link.

export type OneLinkKind =
  | "referral"
  | "invoice"
  | "form"
  | "report"
  | "vault"
  | "invitation"
  | "poll"
  | "proposal"
  | "signal"
  | "market"
  | "whale"
  | "whale_board"
  | "trader"
  | "strategy_template"
  | "strategy"
  | "notification"
  | "route"
  | "collection";

export interface OneLinkTarget {
  kind: OneLinkKind;
  ref?: string;
  scope?: Record<string, unknown>;
}

export interface OneLinkPreview {
  status?: string;
  image_url?: string | null;
  is_fallback?: boolean;
  meme_key?: string | null;
  alt?: string | null;
  caption?: string | null;
  title?: string | null;
  snapshot_at?: string | null;
  version?: number;
}

export interface OneLinkAlias {
  alias_id: string;
  token: string;
  kind: "generated" | "custom";
  url: string;
  disabled: boolean;
  created_at: string;
}

export interface OneLink {
  link_id: string;
  url: string;
  token: string;
  kind: string;
  created_at?: string;
  expires_at?: string | null;
  revoked?: boolean;
  channel?: string | null;
  campaign?: string | null;
  policy?: string;
  preview?: OneLinkPreview | null;
  aliases?: OneLinkAlias[];
  target?: { kind: string; ref: string };
  followup?: Record<string, unknown>;
}

const linkCache = new Map<string, Promise<OneLink>>();

// Twin of isBrandHost in middleware.ts: a host that isn't the app's own is a
// Fexr Desk workspace domain (Cloudflare custom hostname).
const APP_HOST = (() => {
  try {
    return new URL(process.env.NEXT_PUBLIC_APP_URL || "https://app.fexr.io")
      .host;
  } catch {
    return "app.fexr.io";
  }
})();

export function isBrandHost(host: string): boolean {
  const h = host.toLowerCase().split(":")[0];
  return (
    !!h &&
    host.toLowerCase() !== APP_HOST &&
    h !== APP_HOST.split(":")[0] &&
    h !== "localhost" &&
    h !== "127.0.0.1" &&
    !h.endsWith(".pages.dev") &&
    !h.endsWith(".local")
  );
}

/** On a workspace domain, a One Link is shared on that domain: the token path
 *  resolves on any host this app serves (middleware rewrites /{token}). */
export function brandedLinkUrl(url: string): string {
  if (typeof window === "undefined" || !isBrandHost(window.location.host))
    return url;
  try {
    const u = new URL(url);
    if (!/^\/[A-Za-z0-9]{1,48}\/?$/.test(u.pathname)) return url;
    return `${window.location.origin}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

async function apiError(res: Response, fallback: string): Promise<Error> {
  const body = await res.json().catch(() => null);
  const detail = body?.detail;
  const message =
    typeof detail === "string"
      ? detail
      : typeof detail?.message === "string"
        ? detail.message
        : fallback;
  return new Error(message);
}

async function issueOneLink(
  target: OneLinkTarget,
  channel: string | undefined,
  session: unknown,
): Promise<OneLink> {
  const res = await dpopApi.post(
    "/v1/journeys/links",
    {
      target: {
        kind: target.kind,
        ref: target.ref ?? "",
        scope: target.scope ?? {},
      },
      ...(channel ? { channel } : {}),
      locale: getLinkLocale(),
    },
    session,
    { "Idempotency-Key": crypto.randomUUID() },
  );
  if (!res.ok) throw await apiError(res, "Couldn't create a link");
  const data = await res.json();
  if (!data?.link?.url) throw new Error("Couldn't create a link");
  return data.link as OneLink;
}

/** The one-link for a target, created by the API on first use. */
export function getOneLink(
  target: OneLinkTarget,
  session?: unknown,
  opts?: { channel?: string },
): Promise<OneLink> {
  const key = JSON.stringify([
    target.kind,
    target.ref ?? "",
    target.scope ?? {},
    opts?.channel ?? null,
  ]);
  const cached = linkCache.get(key);
  if (cached) return cached;
  const pending = issueOneLink(target, opts?.channel, session).catch(
    (error) => {
      linkCache.delete(key);
      throw error;
    },
  );
  linkCache.set(key, pending);
  return pending;
}

/** Forget cached links, e.g. after a revoke or rename in My links. */
export function forgetOneLinks(): void {
  linkCache.clear();
}

// Copy and share events feed the registry stats (event_version 2, link_id is
// the one-link uuid). Copying also starts the follow-up clock where the link
// kind has a default goal.
export function trackShareAction(
  action: "copied" | "shared",
  link: Pick<OneLink, "link_id"> & { kind?: string },
  surface: string, // e.g. "vault_drawer", "settings", "payments", "report_share"
): void {
  if (typeof window === "undefined" || !link?.link_id) return;
  trackEvent(action === "copied" ? "link_copied" : "link_shared", {
    link_id: link.link_id,
    surface,
    link_type: link.kind,
  });
  if (link.kind === "referral")
    trackEvent("referral.share.completed", { share_result: action });
}

/**
 * Share with the native sheet when asked and available, otherwise copy.
 * Resolves to what happened so callers can toast.
 */
export async function shareOneLink(
  link: OneLink,
  opts: { surface: string; native?: boolean; title?: string; text?: string },
): Promise<"shared" | "copied" | "cancelled"> {
  if (opts.native && typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({
        title: opts.title,
        text: opts.text,
        url: brandedLinkUrl(link.url),
      });
      trackShareAction("shared", link, opts.surface);
      return "shared";
    } catch (error) {
      if ((error as Error)?.name === "AbortError") {
        if (link.kind === "referral")
          trackEvent("referral.share.completed", { share_result: "cancelled" });
        return "cancelled";
      }
    }
  }
  await navigator.clipboard.writeText(brandedLinkUrl(link.url));
  trackShareAction("copied", link, opts.surface);
  return "copied";
}

// ─── My links (Settings → My links) ──────────────────────────────────────────

export interface AliasBudget {
  custom_lifetime_limit: number;
  custom_used: number;
  mutations_per_hour: number;
  renames_per_link_per_day: number;
  availability_checks_per_minute: number;
}

export async function listMyLinks(
  session: unknown,
  cursor?: string | null,
): Promise<{
  items: OneLink[];
  next_cursor: string | null;
  alias_budget: AliasBudget | null;
}> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  const res = await dpopApi.get(`/v1/journeys/links${qs}`, session);
  if (!res.ok) throw await apiError(res, "Couldn't load your links");
  const data = await res.json();
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    next_cursor: data?.next_cursor ?? null,
    alias_budget: data?.alias_budget ?? null,
  };
}

export interface LinkStatsRow {
  link_id: string;
  url: string;
  kind: string;
  opens: number;
  copies: number;
  shares: number;
  authorized_reads: number;
  continuations: number;
  outcomes: number;
  conversion_rate: number | null;
  revoked: boolean;
  last_activity_at: string | null;
}

export async function getMyLinkStats(session: unknown): Promise<{
  registry: LinkStatsRow[];
  definitions: Record<string, string>;
}> {
  const res = await dpopApi.get("/v1/journeys/me/link-stats", session);
  if (!res.ok) throw await apiError(res, "Couldn't load link stats");
  const data = await res.json();
  return {
    registry: Array.isArray(data?.registry) ? data.registry : [],
    definitions: data?.definitions ?? {},
  };
}

export async function getLinkDetail(
  linkId: string,
  session: unknown,
): Promise<any> {
  const res = await dpopApi.get(
    `/v1/journeys/links/${encodeURIComponent(linkId)}`,
    session,
  );
  if (!res.ok) throw await apiError(res, "Couldn't load this link");
  return res.json();
}

export async function getLinkActions(session: unknown): Promise<any[]> {
  const res = await dpopApi.get(
    "/v1/journeys/link-actions?status=active",
    session,
  );
  if (!res.ok) throw await apiError(res, "Couldn't load link actions");
  const data = await res.json();
  return Array.isArray(data?.items) ? data.items : [];
}

export async function checkLinkName(
  token: string,
  session: unknown,
): Promise<"available" | "unavailable" | "invalid"> {
  const res = await dpopApi.get(
    `/v1/journeys/link-tokens/availability?token=${encodeURIComponent(token)}`,
    session,
  );
  if (!res.ok) throw await apiError(res, "Couldn't check that name");
  const data = await res.json();
  return data?.status ?? data?.availability ?? "invalid";
}

export async function addLinkName(
  linkId: string,
  token: string,
  session: unknown,
): Promise<OneLink> {
  const res = await dpopApi.post(
    `/v1/journeys/links/${encodeURIComponent(linkId)}/aliases`,
    { token, make_canonical: true },
    session,
  );
  if (res.status === 409) throw new Error("Someone else just took that name");
  if (!res.ok) throw await apiError(res, "Couldn't add that name");
  forgetOneLinks();
  return res.json();
}

export async function revokeOneLink(
  linkId: string,
  session: unknown,
): Promise<void> {
  const res = await dpopApi.post(
    `/v1/journeys/links/${encodeURIComponent(linkId)}/revoke`,
    {},
    session,
  );
  if (!res.ok) throw await apiError(res, "Couldn't revoke this link");
  forgetOneLinks();
}

// ─── Link language, previews, delegation, templates ───────────────────────────

export const LINK_LOCALES = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "ru", label: "Русский" },
  { value: "ar", label: "العربية" },
  { value: "zh", label: "中文" },
] as const;

const LOCALE_KEY = "fexr:link-locale";

/** Language for the preview card of links created from now on. */
export function getLinkLocale(): string {
  try {
    const v = localStorage.getItem(LOCALE_KEY);
    return LINK_LOCALES.some((l) => l.value === v) ? (v as string) : "en";
  } catch {
    return "en";
  }
}

export function setLinkLocale(locale: string): void {
  try {
    localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    /* storage disabled — links default to English */
  }
  // Links cached for this page were created in the old language.
  linkCache.clear();
}

export interface LinkMemeCatalog {
  styles: Record<string, string[]>;
  ai_illustration: boolean;
}

export async function getLinkMemes(session: unknown): Promise<LinkMemeCatalog> {
  const res = await dpopApi.get("/v1/journeys/link-memes", session);
  if (!res.ok) throw await apiError(res, "Couldn't load preview styles");
  const d = await res.json();
  return { styles: d?.styles ?? {}, ai_illustration: !!d?.ai_illustration };
}

/** Refresh or restyle a link's preview card (3 a day per link). */
export async function restyleLinkPreview(
  linkId: string,
  body: { style?: string; fresh_illustration?: boolean },
  session: unknown,
): Promise<{ status: string; link?: OneLink }> {
  const res = await dpopApi.post(
    `/v1/journeys/links/${linkId}/preview`,
    body,
    session,
  );
  if (!res.ok) throw await apiError(res, "Couldn't change the preview");
  return res.json();
}

/** Assign a link's follow-up to a teammate (null unassigns). */
export async function delegateLinkAction(
  linkId: string,
  assigneeUserId: string | null,
  dueAt: string | null,
  session: unknown,
): Promise<any> {
  const res = await dpopApi.post(
    `/v1/journeys/links/${linkId}/delegate`,
    { assignee_user_id: assigneeUserId, ...(dueAt ? { due_at: dueAt } : {}) },
    session,
  );
  if (!res.ok) throw await apiError(res, "Couldn't assign this");
  return res.json();
}

export type LinkTemplateKind =
  | "invoice"
  | "form"
  | "share_campaign"
  | "invitation";

export interface LinkTemplate {
  template_id: string;
  template_kind: LinkTemplateKind;
  name: string;
  defaults: Record<string, unknown>;
  created_at?: string;
}

export async function listLinkTemplates(
  session: unknown,
): Promise<LinkTemplate[]> {
  const res = await dpopApi.get("/v1/journeys/link-templates", session);
  if (!res.ok) throw await apiError(res, "Couldn't load templates");
  const d = await res.json();
  return Array.isArray(d?.templates) ? d.templates : [];
}

export async function saveLinkTemplate(
  body: {
    kind: LinkTemplateKind;
    name: string;
    defaults: Record<string, unknown>;
  },
  session: unknown,
): Promise<LinkTemplate> {
  const res = await dpopApi.post("/v1/journeys/link-templates", body, session);
  if (!res.ok) throw await apiError(res, "Couldn't save the template");
  return res.json();
}

export async function deleteLinkTemplate(
  templateId: string,
  session: unknown,
): Promise<void> {
  const res = await dpopApi.delete(
    `/v1/journeys/link-templates/${templateId}`,
    session,
  );
  if (!res.ok) throw await apiError(res, "Couldn't delete the template");
}

/** Create a new draft from a template; the result is for review, not sent. */
export async function instantiateLinkTemplate(
  templateId: string,
  inputs: Record<string, unknown>,
  session: unknown,
): Promise<any> {
  const res = await dpopApi.post(
    `/v1/journeys/link-templates/${templateId}/instantiate`,
    { inputs },
    session,
    { "Idempotency-Key": crypto.randomUUID() },
  );
  if (!res.ok) throw await apiError(res, "Couldn't use the template");
  return res.json();
}

/**
 * Disable one name of a link (kept as a tombstone, never reused). Disabling
 * the canonical name needs a replacement that becomes canonical.
 */
export async function disableLinkAlias(
  linkId: string,
  aliasId: string,
  replacementAliasId: string | null,
  session: unknown,
): Promise<OneLink> {
  const res = await dpopApi.post(
    `/v1/journeys/links/${linkId}/aliases/${aliasId}/disable`,
    replacementAliasId ? { replacement_alias_id: replacementAliasId } : {},
    session,
  );
  if (!res.ok) throw await apiError(res, "Couldn't disable this name");
  return (await res.json()).link as OneLink;
}
